import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  HomeIcon,
  ShoppingCartIcon,
  BellIcon,
  UserIcon,
  ArrowRightStartOnRectangleIcon,
  AcademicCapIcon,
  BanknotesIcon,
  UsersIcon,
  EllipsisHorizontalIcon,
  ClockIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { useCart } from "../context/cartContext";
import { checkNotificationPermission, subscribeUserToPush } from "../utils/notificationUtil";
import { BRAND_LOGO, BRAND_NAME } from "../utils/brandAssets";

const UserNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getItemCount } = useCart();
  const itemCount = getItemCount();
  const [isNotificationsEnabled, setIsNotificationsEnabled] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showMobileMoreMenu, setShowMobileMoreMenu] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [lastNotificationCheck, setLastNotificationCheck] = useState(null);

  useEffect(() => {
    const checkPermission = async () => {
      const hasPermission = await checkNotificationPermission();
      setIsNotificationsEnabled(hasPermission);
    };

    checkPermission();
    
    // Initialize last notification check from localStorage
    const lastCheck = localStorage.getItem('lastNotificationCheck');
    if (lastCheck) {
      setLastNotificationCheck(parseInt(lastCheck));
    }
  }, []);

  // Simulate notification tracking (you can replace this with actual API calls)
  useEffect(() => {
    const checkForNewNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;

        // This is a placeholder - replace with your actual notification API endpoint
        // const response = await fetch('/api/notifications/count', {
        //   headers: { 'Authorization': `Bearer ${token}` }
        // });
        // const data = await response.json();
        
        // For now, simulate new notifications (you can remove this and use real API)
        const simulatedNewCount = Math.floor(Math.random() * 5); // 0-4 new notifications
        const lastCheck = lastNotificationCheck || Date.now();
        
        // Only show count if there are new notifications since last check
        if (Date.now() - lastCheck > 60000) { // More than 1 minute since last check
          setNotificationCount(simulatedNewCount);
        }
      } catch (error) {
        console.error('Error checking notifications:', error);
      }
    };

    // Check for notifications every 30 seconds
    const interval = setInterval(checkForNewNotifications, 30000);
    
    // Initial check
    checkForNewNotifications();

    return () => clearInterval(interval);
  }, [lastNotificationCheck]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMoreMenu && !event.target.closest('.mobile-more-menu-container')) {
        setShowMobileMoreMenu(false);
      }
      if (showProfileMenu && !event.target.closest('.profile-menu-container')) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMoreMenu, showProfileMenu]);

  const getLinkClassName = (path) =>
    location.pathname === path
      ? "text-cheese font-bold"
      : "text-white hover:text-cheese transition-all duration-300";

  const getMobileLinkClassName = (path) =>
    location.pathname === path
      ? "bg-pepperoni text-white shadow-lg scale-105"
      : "text-white hover:bg-crust/20 transition-all duration-300";

  const handleLogout = () => {
    if (window.confirm("Do you really want to log out?")) {
      localStorage.removeItem("token");
      localStorage.removeItem('byteUser');
      navigate("/login");
    }
  };

  const handleEnableNotifications = async () => {
    const subscription = await subscribeUserToPush();
    if (subscription) {
      setIsNotificationsEnabled(true);
    }
  };

  return (
    <>
      {/* Desktop Navigation */}
      <nav className="bg-crust shadow-lg fixed w-full top-0 z-50 hidden md:block">
        <div className="w-full px-6 py-3">
          <div className="flex items-center justify-between">
            <Link to="/user" className="flex items-center space-x-3 text-white hover:opacity-90 transition-opacity duration-300">
              <motion.img
                src={BRAND_LOGO}
                alt="Byte Logo"
                className="w-10 h-10"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              />
              <div className="flex flex-col">
                <span className="text-2xl font-bold">{BRAND_NAME}</span>
                <span className="text-xs text-cheese flex items-center">
                  <AcademicCapIcon className="w-3 h-3 mr-1" />
                  Campus Food Delivery
                </span>
              </div>
            </Link>

            <div className="flex items-center space-x-6">
              <Link
                to="/user"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${getLinkClassName("/user")}`}
              >
                <HomeIcon className="w-5 h-5" />
                <span className="font-medium">Home</span>
              </Link>

              <Link
                to="/user/cart"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 relative ${getLinkClassName("/user/cart")}`}
              >
                <ShoppingCartIcon className="w-5 h-5" />
                <span className="font-medium">Order</span>
                {itemCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-pepperoni rounded-full text-white text-xs font-bold"
                  >
                    {itemCount}
                  </motion.span>
                )}
              </Link>

              <Link
                to="/user/notifs"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 relative ${getLinkClassName("/user/notifs")}`}
                onClick={() => {
                  // Reset notification count when user opens notifications
                  setNotificationCount(0);
                  setLastNotificationCheck(Date.now());
                  localStorage.setItem('lastNotificationCheck', Date.now().toString());
                }}
              >
                <BellIcon className="w-5 h-5" />
                <span className="font-medium">Notifications</span>
                {notificationCount > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full border-2 border-white"
                  />
                )}
              </Link>

              <Link
                to="/user/fund"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${getLinkClassName("/user/fund")}`}
              >
                <BanknotesIcon className="w-5 h-5" />
                <span className="font-medium">Fund</span>
              </Link>

              <Link
                to="/user/profile"
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${getLinkClassName("/user/profile")}`}
              >
                <UserIcon className="w-5 h-5" />
                <span className="font-medium">Profile</span>
              </Link>

              <div className="relative profile-menu-container">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center space-x-2 px-4 py-2 rounded-xl text-white hover:text-cheese transition-all duration-300"
                >
                  <EllipsisHorizontalIcon className="w-5 h-5" />
                  <span className="font-medium">More</span>
                </button>

                <AnimatePresence>
                  {showProfileMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 rounded-xl shadow-xl bg-white border border-gray-100 py-2 z-50"
                    >

                      <Link
                        to="/user/transactions"
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <BanknotesIcon className="w-4 h-4 inline mr-2" />
                        Transaction History
                      </Link>

                      <Link
                        to="/user/orderhistory"
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <ClockIcon className="w-4 h-4 inline mr-2" />
                        Order History
                      </Link>
                      <Link
                        to="/user/advanced-orders"
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <SparklesIcon className="w-4 h-4 inline mr-2" />
                        Advanced Orders
                      </Link>
                      <Link
                        to="/user/payment-requests"
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <UsersIcon className="w-4 h-4 inline mr-2" />
                        Payment Requests
                      </Link>
                      <Link
                        to="/user/referrals"
                        className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        onClick={() => setShowProfileMenu(false)}
                      >
                        <AcademicCapIcon className="w-4 h-4 inline mr-2" />
                        Referrals
                      </Link>
                      {!isNotificationsEnabled && (
                        <button
                          onClick={() => {
                            handleEnableNotifications();
                            setShowProfileMenu(false);
                          }}
                          className="flex items-center w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                        >
                          <BellIcon className="w-4 h-4 inline mr-2" />
                          Enable Notifications
                        </button>
                      )}
                      <hr className="my-1" />
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowProfileMenu(false);
                        }}
                        className="flex items-center w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                      >
                        <ArrowRightStartOnRectangleIcon className="w-4 h-4 inline mr-2" />
                        Logout
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Top Bar */}
      <nav className="bg-crust shadow-lg fixed w-full top-0 z-40 flex md:hidden ps-3">
        <div className="w-full px-4 py-4">
          <div className="flex items-center">
            <Link
              to="/"
              className="flex items-center space-x-2 text-white"
            >
              <img
                src={BRAND_LOGO}
                alt="Byte Logo"
                className="w-9 h-9"
              />
              <div className="flex flex-col">
                <span className="text-base font-bold">{BRAND_NAME}</span>
                <span className="text-xs text-cheese flex items-center">
                  <AcademicCapIcon className="w-2 h-2 mr-1" />
                  Campus Delivery
                </span>
              </div>
            </Link>
          </div>
        </div>
      </nav>

      {/* Mobile Bottom Navigation */}
      <motion.nav
        className="fixed inset-x-0 bottom-0 z-50 bg-crust md:hidden shadow-2xl border-t border-gray-800"
        initial={{ y: 100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <div className="px-1 py-1 overflow-x-auto">
          <div className="flex items-center min-w-max" style={{ minWidth: '100%' }}>
            <div className="flex justify-around items-center w-full min-w-[320px]">
            <Link
              to="/user"
              className={`flex flex-col items-center justify-center px-2 sm:px-3 py-2 sm:py-3 rounded-xl transition-all duration-300 ${getMobileLinkClassName("/user")}`}
            >
              <HomeIcon className="w-5 h-5 sm:w-6 sm:h-6 mb-1 text-current" />
              <span className="text-xs font-medium text-current">Home</span>
            </Link>

            <Link
              to="/user/cart"
              className={`flex flex-col items-center justify-center px-2 sm:px-3 py-2 sm:py-3 rounded-xl transition-all duration-300 relative ${getMobileLinkClassName("/user/cart")}`}
            >
              <ShoppingCartIcon className="w-5 h-5 sm:w-6 sm:h-6 mb-1 text-current" />
              <span className="text-xs font-medium text-current">Order</span>
              {itemCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-0 right-0 flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 bg-pepperoni rounded-full text-white text-xs font-bold"
                >
                  {itemCount}
                </motion.span>
              )}
            </Link>

            <Link
              to="/user/notifs"
              className={`flex flex-col items-center justify-center px-2 sm:px-3 py-2 sm:py-3 rounded-xl transition-all duration-300 relative ${getMobileLinkClassName("/user/notifs")}`}
              onClick={() => {
                // Reset notification count when user opens notifications
                setNotificationCount(0);
                setLastNotificationCheck(Date.now());
                localStorage.setItem('lastNotificationCheck', Date.now().toString());
              }}
            >
              <BellIcon className="w-5 h-5 sm:w-6 sm:h-6 mb-1 text-current" />
              <span className="text-xs font-medium text-current">Notifs</span>
              {notificationCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white"
                />
              )}
            </Link>

            <Link
              to="/user/fund"
              className={`flex flex-col items-center justify-center px-2 sm:px-3 py-2 sm:py-3 rounded-xl transition-all duration-300 ${getMobileLinkClassName("/user/fund")}`}
            >
              <BanknotesIcon className="w-5 h-5 sm:w-6 sm:h-6 mb-1 text-current" />
              <span className="text-xs font-medium text-current">Fund</span>
            </Link>

            <Link
              to="/user/profile"
              className={`flex flex-col items-center justify-center px-2 sm:px-3 py-2 sm:py-3 rounded-xl transition-all duration-300 ${getMobileLinkClassName("/user/profile")}`}
            >
              <UserIcon className="w-5 h-5 sm:w-6 sm:h-6 mb-1 text-current" />
              <span className="text-xs font-medium text-current">Profile</span>
            </Link>

            {/* More Button */}
            <div className="relative mobile-more-menu-container">
              <button
                onClick={() => setShowMobileMoreMenu(!showMobileMoreMenu)}
                className={`flex flex-col items-center justify-center px-2 sm:px-3 py-2 sm:py-3 rounded-xl transition-all duration-300 ${showMobileMoreMenu ||
                    location.pathname === "/user/notifs" ||
                    location.pathname === "/user/payment-requests" ||
                    location.pathname === "/user/orderhistory" ||
                    location.pathname === "/user/order-history" ||
                    location.pathname === "/user/transactions" ||
                    location.pathname === "/user/advanced-orders" ||
                    location.pathname === "/user/referrals"
                    ? "bg-pepperoni text-white shadow-lg scale-105"
                    : "text-white hover:bg-crust/20 transition-all duration-300"
                  }`}
              >
                <EllipsisHorizontalIcon className="w-5 h-5 sm:w-6 sm:h-6 mb-1 text-current" />
                <span className="text-xs font-medium text-current">More</span>
              </button>

              {/* Mobile More Menu */}
              <AnimatePresence>
                {showMobileMoreMenu && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="fixed bottom-16 right-2 w-44 max-w-[calc(100vw-1rem)] rounded-xl shadow-2xl bg-accentwhite border-2 border-secondary-200 py-2 z-[60]"
                  >


                    <Link
                      to="/user/transactions"
                      className="flex items-center px-4 py-3 text-sm text-crust hover:bg-secondary-50 transition-colors duration-200 font-sans"
                      onClick={() => setShowMobileMoreMenu(false)}
                    >
                      <BanknotesIcon className="w-5 h-5 inline mr-3 text-pepperoni" />
                      <span className="font-medium">Transaction History</span>
                    </Link>

                    <Link
                      to="/user/orderhistory"
                      className="flex items-center px-4 py-3 text-sm text-crust hover:bg-secondary-50 transition-colors duration-200 font-sans"
                      onClick={() => setShowMobileMoreMenu(false)}
                    >
                      <ClockIcon className="w-5 h-5 inline mr-3 text-pepperoni" />
                      <span className="font-medium">Order History</span>
                    </Link>

                    <Link
                      to="/user/advanced-orders"
                      className="flex items-center px-4 py-3 text-sm text-crust hover:bg-secondary-50 transition-colors duration-200 font-sans"
                      onClick={() => setShowMobileMoreMenu(false)}
                    >
                      <SparklesIcon className="w-5 h-5 inline mr-3 text-pepperoni" />
                      <span className="font-medium">Advanced Orders</span>
                    </Link>

                    <Link
                      to="/user/payment-requests"
                      className="flex items-center px-4 py-3 text-sm text-crust hover:bg-secondary-50 transition-colors duration-200 font-sans"
                      onClick={() => setShowMobileMoreMenu(false)}
                    >
                      <UsersIcon className="w-5 h-5 inline mr-3 text-pepperoni" />
                      <span className="font-medium">Payment Requests</span>
                    </Link>

                    <Link
                      to="/user/referrals"
                      className="flex items-center px-4 py-3 text-sm text-crust hover:bg-secondary-50 transition-colors duration-200 font-sans"
                      onClick={() => setShowMobileMoreMenu(false)}
                    >
                      <AcademicCapIcon className="w-5 h-5 inline mr-3 text-pepperoni" />
                      <span className="font-medium">Referrals</span>
                    </Link>

                    <hr className="my-1 border-secondary-200" />
                    <button
                      onClick={() => {
                        handleLogout();
                        setShowMobileMoreMenu(false);
                      }}
                      className="flex items-center w-full text-left px-4 py-3 text-sm text-pepperoni hover:bg-primary-50 transition-colors duration-200 font-sans"
                    >
                      <ArrowRightStartOnRectangleIcon className="w-5 h-5 inline mr-3" />
                      <span className="font-medium">Logout</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Content padding to avoid navbar overlap */}
      <div className="pt-12 md:pt-16"></div>
    </>
  );
};

export default UserNavbar;